import { ProductsWhereSearchDocument, type Product, type ProductsWhereSearchQuery } from '~/graphql/operations'
import { BaseSelect } from '../model'

export class ProductExtSelect extends BaseSelect {
  override name = 'product'
  override label = 'Product'
  override placeholder = 'Select product'
  // mode = "tags"
  override view = 'product'
  override defaultTextPriority?: boolean = false
  override resolveOnLoad = false

  // Add caching
  private cachedOptions: any = null
  private lastQuery: string = ''

  constructor(args: Partial<ProductExtSelect>) {
    super(args as any)
    // New Select
    return this.products(args.view === 'full' ? 'full' : 'compact')
  }

  override where(data) {
    return data
  }

  productToProductData(item: Product) {
    // Create a simplified object with just the necessary fields
    // Make sure to include all fields needed by ProductOptionCompact.slot.vue
    return {
      id: item.id,
      productNumber: item.productNumber || '',
      supplierProductNumber: item.supplierProductNumber,
      defaultText: {
        name: item.defaultText?.name || 'Unknown Product',
      },
      // Add manufacturer with defaultText to prevent errors
      manufacturer: {
        defaultText: {
          name:
            item.manufacturer?.defaultText?.name || 'Unknown Manufacturer',
        },
        deliveryDays: item.manufacturer?.deliveryDays,
      },
      defaultImage: {
        image: {
          fileUrl: item.defaultImage?.image?.fileUrl || null,
        },
      },
      defaultStock: {
        available: item.defaultStock?.available,
        sellableStock: item.defaultStock?.sellableStock,
        availableStock: item.defaultStock?.availableStock
      },
      // Add productPrice to prevent errors
      productPrice: item.productPrice || null,
    }
  }

  mapData(data: ProductsWhereSearchQuery) {
    // Ensure each item has a properly formatted name for JSON parsing
    data?.productsCompact?.data.forEach((item: Product) => {
      // Create a simplified object with all necessary fields
      (item as any).name = JSON.stringify(
        this.productToProductData(item)
      )
    })
    return data
  }


  // Override the recordToSelectItem method to ensure proper formatting
  override recordToSelectItem(item: Product) {
    const value = +item.id

    // For product-compact view, ensure we have a valid JSON string
    if (this.view === 'product-compact') {
      const label = JSON.stringify(this.productToProductData(item))
      return {
        value,
        label,
      }
    }

    return {
      value,
      label: this.recordToLabel(item),
    }
  }

  override async options(query?: string, where?) {
    // Remove because of empty lists all time
    // if (!query?.trim()) {
    //   return []
    // }

    // Return cached results if query hasn't changed
    if (this.cachedOptions && query === this.lastQuery) {
      return this.cachedOptions
    }

    this.lastQuery = query

    // Ensure we have a valid channelId
    const channelId = where?.channelId ? +where.channelId : undefined

    // If no channelId is available, log a warning and return empty results
    if (!channelId) {
      console.warn('ProductExtSelect: No channelId provided for product search')
      // return []
    }

    const result = await this.optionsQuery(
      ProductsWhereSearchDocument,
      'productsCompact',
      {
        variables: {
          where: {
            search: query,
            searchTags: [
              'productName',
              'productNumber',
              'supplierProductNumber',
            ],
            productType: where.productType ? {
              equals: where.productType
            } : undefined, channelId: channelId,
          },
          channelId: channelId,
          translationChannelId: channelId,
          page: 1,
          perPage: 50,
        },
        mapData: (d) => this.mapData(d)
      },
    )

    this.cachedOptions = result
    return result
  }

  override async initialOptions(ids?: any[], where?) {
    if (!ids?.length) return []
    // Filter out null/undefined/invalid IDs and ensure they are numbers
    const validIds = ids.filter((id) => id != null && !isNaN(+id))
    if (!validIds.length) return []

    // Get the store to access channels if where is undefined
    const store = useStore()

    // Try to get channelId from multiple sources
    let channelId: number | undefined

    if (where?.channelId) {
      // If where has channelId, use it
      channelId = +where.channelId
    } else {
      // Try to get from route
      const route = useRoute()
      if (route.query?.channel) {
        channelId = +route.query.channel
      } else {
        // Fallback to first channel in store
        channelId = store.channels?.at(0)?.id
      }
    }

    // If no channelId is available, log a warning and return empty results
    if (!channelId) {
      console.warn(
        'ProductExtSelect: No channelId provided for initial product options',
      )
      return []
    }

    return this.optionsQuery(ProductsWhereSearchDocument, 'productsCompact', {
      variables: {
        where: {
          channelId: channelId,
          id: {
            in: validIds.map((i) => +i),
          },
          productType: where.productType ? {
            equals: where.productType
          } : undefined,
        },
        channelId: channelId,
        translationChannelId: channelId,

        page: 1,
        perPage: 50,
      },
      mapData: (d) => this.mapData(d),
    })
  }
}
